import { stopIncomingCallRingtone } from '../util/playIncomingCallRingtone';
import { dismissIncomingCallNotification } from '../notifications/androidChannels';
import { replaceIncomingWithMissedCallNotification } from '../notifications/callNotifications';
import { takePendingCallUuidsForChat } from '../notifications/incomingCallBridge';
import { dismissIncomingCallUI } from './incomingCallUiBridge';
import { getActiveCall } from './activeCallRegistry';
import { normCallChatId } from './callBusyRules';
import { endSystemIncomingCallUi } from './endSystemCallUi';
import type { RingCancelReason } from './incomingRingCancelRules';
import { clearRingingIncomingCall } from './ringingIncomingCallRegistry';
import { voiceCallLog } from './voiceCallDebug';
import { logCallLifecycleEnd } from './callLifecycleDebug';

/**
 * Погасить звонящий входящий во всех четырёх местах, где он может звонить.
 *
 * <p>Их действительно четыре, и это не избыточность: рингтон играет
 * `react-native-sound`, экран рисует либо наш оверлей (Android, веб), либо системный
 * CallKit/Telecom (iOS и тапнутое уведомление), а в шторке отдельно висит полноэкранное
 * уведомление со <b>своим</b> зациклённым звуком. Погасив три из четырёх, получаешь
 * телефон, который продолжает звонить, — ровно то, на что жаловались.
 *
 * <p>Функция идемпотентна: отмена того же звонка вторым каналом (WS-кадр и следом пуш) —
 * это норма, а не ошибка.
 */
export async function cancelRingingIncomingCall(
  chatId: string,
  reason: RingCancelReason,
): Promise<void> {
  /**
   * В этом чате у нас уже идёт разговор — значит, входящий здесь не звонит, и гасить нечего.
   *
   * <p>Проверка не формальность. Отмену вызывает в том числе `bye`, а `bye` прилетает в конце
   * <b>каждого</b> разговора. Без этой ветки конец собственного звонка уводил бы нас в
   * закрытие системного экрана того самого звонка, который в эту секунду ещё живёт, — и
   * завершал бы его мимо обычного пути, не вернув аудиосессию.
   */
  const active = getActiveCall();
  if (active && normCallChatId(active.chatId) === normCallChatId(chatId) && active.phase !== 'ringing') {
    voiceCallLog('отмена входящего пропущена: в этом чате идёт свой звонок', {
      chatId,
      phase: active.phase,
    });
    return;
  }
  const entry = clearRingingIncomingCall(chatId);
  voiceCallLog('входящий отменён', { chatId, reason, hadEntry: !!entry });
  logCallLifecycleEnd('incoming_ring_cancelled', {
    chatId,
    callId: entry?.inviteId ?? null,
    extra: { reason },
  });

  await stopIncomingCallRingtone();
  dismissIncomingCallUI(chatId);
  if (reason === 'caller_hung_up') {
    // Звонящий передумал, а звонок висел в шторке (приложение свёрнуто или свёрнут «Назад») —
    // человек его не видел: оставляем «Пропущенный» с «Перезвонить». Строго до снятия входящего.
    await replaceIncomingWithMissedCallNotification(chatId);
  }
  await dismissIncomingCallNotification();

  // UUID системного экрана заводились обоими путями — и VoIP-пушем, и WS-кадром; берём
  // всё, что знает реестр, и всё, что осталось в хранилище pending-звонков.
  const uuids = new Set<string>(entry?.callUuids ?? []);
  for (const uuid of takePendingCallUuidsForChat(chatId)) {
    uuids.add(uuid);
  }
  endSystemIncomingCallUi([...uuids], reason);
}
